import createQuoteLineId from "../../lib/createQuoteLineId";
import { useNavigate, useParams } from "react-router-dom"
import { Box, Chip, CircularProgress, Tab, Tabs } from "@mui/material"
import { Category, Description, Handyman, Info, LocalShipping, LockOutlined, Tune } from "@mui/icons-material"
import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../../lib/api";
import SoldTo from "../UniqueQuote/SoldTo";
import SaleInfo from "../UniqueQuote/SaleInfo";
import Notes from "../UniqueQuote/Notes";
import Custom from "../UniqueQuote/Custom";
import QuoteItems from "../UniqueQuote/QuoteItems";
import Tools from "../UniqueQuote/Tools";
import Feedback from "../../components/Feedback/Feedback";
import BaseWrapper from "../../components/BaseWrapper/BaseWrapper"
import { useAuth } from "../../contexts/AuthContext";

const hasQuoteEditPermission = (user) => Boolean(user);

const isIndex = (s) => /^\d+$/.test(String(s));

function setDeep(obj, path, value) {
  const keys = path.split(".");
  const next = structuredClone(obj ?? {});
  let cur = next;

  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const nextK = keys[i + 1];

    if (isIndex(k)) {
      const idx = Number(k);
      if (!Array.isArray(cur)) throw new Error(`Expected array at ${keys.slice(0, i).join(".")}`);
      if (cur[idx] == null) cur[idx] = isIndex(nextK) ? [] : {};
      cur = cur[idx];
    } else {
      if (cur[k] == null) cur[k] = isIndex(nextK) ? [] : {};
      cur = cur[k];
    }
  }

  const last = keys[keys.length - 1];
  if (isIndex(last)) {
    if (!Array.isArray(cur)) throw new Error(`Expected array at ${keys.slice(0, -1).join(".")}`);
    cur[Number(last)] = value;
  } else {
    cur[last] = value;
  }

  return next;
}

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            className="hive-base-unique-quote-tab-panel"
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const BaseUniqueQuote = ({ readOnly = false }) => {
    const [value, setValue] = useState(0);
    const [quote,setQuote] = useState({})
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)
    const [feedback, setFeedback] = useState({})
    const { qid } = useParams()
    const navigate = useNavigate();
    const { user } = useAuth();
    const canEditQuote = hasQuoteEditPermission(user) && quote?.canEdit !== false;

    const handleChange = (event, newValue) => setValue(newValue);

    const debounceTimerRef = useRef(null);
    const pendingPatchRef = useRef({});
    const abortRef = useRef(null);
    const activeSavePromiseRef = useRef(null);
    const saveSeqRef = useRef(0);
    const latestAppliedSeqRef = useRef(0);

    const handleRevision = async () => {
        const response = await API.Quotes.CreateRevision(qid, { "createdBy": localStorage.id })
        if (response.status === 200) {
            navigate(`/base-unique-quote/${response?.data?._id}`)
            setFeedback({open: true, message: "Revision Successfully Created! Data Refreshed", type: "success"})
        }
        else {
            setFeedback({open: true, message: "Failed to create rev...", type: "error"})
        }
    }

    const handleDeliver = async () => {
        await flushQuotePatch();
        const res = await API.Quotes.DeliveryPDF(qid);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = quote?.quoteNumber  ? `${quote.quoteNumber}.pdf` : `quote-${qid}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
    };

    const handleDeliverNoPricing = async () => {
        await flushQuotePatch();
        const res = await API.Quotes.DeliveryPDFNoPricing(qid);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = quote?.quoteNumber ? `${quote.quoteNumber}-no-pricing.pdf` : `quote-${qid}-no-pricing.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
    };

    const handleDeliverConfirmation = async () => {
        await flushQuotePatch();
        const res = await API.Quotes.DeliveryOrderConfirmation(qid);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = quote?.quoteNumber  ? `${quote.projectNumber} Order Confirmation.pdf` : `Order Confirmation.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
    };

    const saveQuotePatch = useCallback(async (payload) => {
        if (!payload || !Object.keys(payload).length) {
            return null;
        }

        const ops = Object.entries(payload).map(([path, value]) => ({ path, value }));
        const mySeq = ++saveSeqRef.current;
        let savePromise = null;

        try {
            if (abortRef.current) abortRef.current.abort();
            abortRef.current = new AbortController();

            savePromise = API.Quotes.Update(qid, { ops }, {
                signal: abortRef.current.signal,
            });

            activeSavePromiseRef.current = savePromise;

            const updated = await savePromise;

            if (mySeq < saveSeqRef.current) {
                return updated?.data ?? null;
            }

            latestAppliedSeqRef.current = mySeq;

            setQuote((prev) => {
                const hasPending = Object.keys(pendingPatchRef.current).length > 0;
                if (hasPending) return prev;
                return updated?.data ?? prev;
            });

            return updated?.data ?? null;
        } catch (err) {
            if (err?.name === "AbortError") return null;
            console.error("Failed to autosave quote:", err);
            return null;
        } finally {
            if (activeSavePromiseRef.current === savePromise) {
                activeSavePromiseRef.current = null;
            }
        }
    }, [qid]);

    const patchQuote = useCallback((partial) => {
        setQuote((prev) => {
            let next = prev;

            for (const [key, val] of Object.entries(partial)) {
                if (key.includes(".")) {
                    next = setDeep(next, key, val);
                } else {
                    next = { ...next, [key]: val };
                }
            }

            return next;
        });

        pendingPatchRef.current = { ...pendingPatchRef.current, ...partial };

        clearTimeout(debounceTimerRef.current);

        debounceTimerRef.current = setTimeout(async () => {
            const payload = pendingPatchRef.current;
            pendingPatchRef.current = {};
            await saveQuotePatch(payload);
        }, 600);
    }, [saveQuotePatch, setQuote]);

    const addProductsToQuote = useCallback((selectedProducts) => {
        const lines = Array.isArray(quote?.lines) ? quote.lines : [];

        const maxLineNumber =
            lines.length === 0 ? 0 : Math.max(...lines.map(l => Number(l.lineNumber) || 0));

        const newLines = (selectedProducts || []).map((p, idx) => {
            const qty = 1;
            const cost = Number(p.cost) || 0;
            const price = Number(p.price) || 0;

            return {
                _id: createQuoteLineId(),
                lineNumber: maxLineNumber + idx + 1,
                type: "item",
                productId: p._id,
                manufacturerPartNumber: p.manufacturerPartNumber || "",
                descriptionTitle: p.description || "",
                descriptionBody: p.descriptionBody || "",
                quantity: qty,
                cost,
                extCost: Number((qty * cost).toFixed(2)),
                price,
                extPrice: Number((qty * price).toFixed(2)),
                isSubtotal: false,
                isDiscount: false,
            };
        });

        patchQuote({ lines: [...lines, ...newLines] });
    }, [quote?.lines, patchQuote]);

    const flushQuotePatch = useCallback(async () => {
        clearTimeout(debounceTimerRef.current);
        const payload = pendingPatchRef.current;
        pendingPatchRef.current = {};
        if (Object.keys(payload).length) {
            return await saveQuotePatch(payload);
        }

        if (activeSavePromiseRef.current) {
            return await activeSavePromiseRef.current;
        }

        return null;
    }, [saveQuotePatch]);

    useEffect(() => {
        return () => {
            clearTimeout(debounceTimerRef.current);
        };
    }, []);

    useEffect(() => {
        async function fetchData() {
            const [quoteResponse, usersResponse] = await Promise.all([
                API.Quotes.FetchOne(qid),
                API.Users.FetchAll(),
            ])

            setQuote(quoteResponse)
            setUsers(usersResponse.map((userRecord) => ({
                value: userRecord._id,
                label: `${userRecord.firstName} ${userRecord.lastName}`,
            })))
            setLoading(false)
        }
        fetchData()
    },[qid])

    useEffect(() => {
        if (loading) return;

        if (!readOnly && !canEditQuote) {
            navigate(`/base-unique-quote-read-only/${qid}`, { replace: true });
            return;
        }

        if (readOnly && canEditQuote) {
            navigate(`/base-unique-quote/${qid}`, { replace: true });
        }
    }, [canEditQuote, loading, navigate, qid, readOnly]);

    const content = loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
        </Box>
    ) : (
        <Box className="hive-base-unique-quote" sx={{ width: '100%' }}>
            <Feedback props={feedback} setFeedback={setFeedback} />

            {readOnly && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                    <Chip
                        icon={<LockOutlined />}
                        label="Read Only"
                        color="warning"
                        variant="outlined"
                    />
                </Box>
            )}

            <Box className="hive-base-unique-quote-tabs-shell" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs className="hive-base-unique-quote-tabs" value={value} onChange={handleChange} aria-label="basic tabs example">
                    <Tab className="hive-base-unique-quote-tab" icon={<Category />} label="Items" {...a11yProps(0)} />
                    <Tab className="hive-base-unique-quote-tab" icon={<LocalShipping />} label="Sold-To/Ship-To" {...a11yProps(1)} />
                    <Tab className="hive-base-unique-quote-tab" icon={<Info />} label="Sale Info" {...a11yProps(2)} />
                    <Tab className="hive-base-unique-quote-tab" icon={<Description />} label="Notes" {...a11yProps(3)} />
                    <Tab className="hive-base-unique-quote-tab" icon={<Tune />} label="Custom" {...a11yProps(4)} />
                    <Tab className="hive-base-unique-quote-tab" icon={<Handyman />} label="Tools" {...a11yProps(5)} />
                </Tabs>
            </Box>

            <CustomTabPanel value={value} index={0}>
                <QuoteItems quote={quote} patchQuote={patchQuote} onAddProducts={addProductsToQuote} readOnly={readOnly} />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
                <SoldTo quote={quote} patchQuote={patchQuote} flushQuotePatch={flushQuotePatch} readOnly={readOnly} />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
                <SaleInfo quote={quote} patchQuote={patchQuote} flushQuotePatch={flushQuotePatch} users={users} readOnly={readOnly} />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={3}>
                <Notes quote={quote} patchQuote={patchQuote} flushQuotePatch={flushQuotePatch} readOnly={readOnly} />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={4}>
                <Custom
                    quote={quote}
                    patchQuote={patchQuote}
                    flushQuotePatch={flushQuotePatch}
                    users={users}
                    readOnly={readOnly}
                />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={5}>
                <Tools onDeliver={handleDeliver} onDeliverNoPricing={handleDeliverNoPricing} onRevision={handleRevision} handleDeliverConfirmation={handleDeliverConfirmation} readOnly={readOnly} />
            </CustomTabPanel>
        </Box>
    );

    return <BaseWrapper content={content} />
}

export default BaseUniqueQuote
